import { federalStateLabels } from "@lib/labels";
import {
  FormDefinition,
  FormFieldDefinition,
} from "@modules/application/components/DynamicForm";
import { TFunction } from "i18next";
import {
  employmentTypeLabels,
  propertyTypeLabels,
  rangeLabels,
} from "../formatting";
import { BudgetCheckFormValues, YesNo } from "./types";

export const getPropertyTypeStep = (
  t: TFunction,
): FormDefinition<BudgetCheckFormValues> => {
  return {
    submit: true,
    title: t("property_type_title"),
    fields: [
      {
        label: t("property_type_label"),
        component: "SELECT",
        name: "searchProfile.propertyType",
        defaultValue: "APARTMENT",
        options: [
          ...Object.keys(propertyTypeLabels)
            .map((key) => ({
              value: key,
              label: t(propertyTypeLabels[key]),
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
          {
            value: "OTHER",
            label: "Sonstiges",
          },
        ],
        rules: {
          required: { value: true, message: t("property_type_error") },
        },
      },
      {
        label: t("optional_label", { label: t("rooms_label") }),
        component: "RANGE",
        name: "searchProfile.rooms",
        placeholder: t("indifferent"),
      },
      {
        label: t("optional_label", { label: t("squaremeter_label") }),
        component: "RANGE",
        name: "searchProfile.squaremeter",
        placeholder: t("indifferent"),
      },
    ],
  };
};

export const getFederalStateStep = (
  t: TFunction,
  searchProfile?: boolean,
): FormDefinition<BudgetCheckFormValues> => {
  const fields: FormFieldDefinition<BudgetCheckFormValues>[] = [];

  if (searchProfile) {
    fields.push(
      {
        label: t("place_label"),
        component: "MAPS_COMBOBOX",
        name: "searchProfile.placeId",
        rules: {
          required: { value: true, message: t("place_error") },
        },
        placeholder: t("place_placeholder"),
        emptyMessage: t("place_empty_message"),
      },
      {
        label: t("range_label"),
        component: "SELECT",
        name: "searchProfile.range",
        defaultValue: "5",
        options: Object.keys(rangeLabels).map((key) => ({
          value: key,
          label: rangeLabels[key],
        })),
        rules: {
          required: { value: true, message: t("range_error") },
        },
      },
    );
  } else {
    fields.push(
      {
        label: t("federal_state_label"),
        component: "SELECT",
        placeholder: t("federal_state_placeholder"),
        name: "federalState",
        options: Object.keys(federalStateLabels)
          .map((key) => ({
            value: key,
            label: federalStateLabels[key],
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        rules: {
          required: { value: true, message: t("federal_state_error") },
        },
      },
      {
        component: "RADIO_CARD",
        options: [
          { label: "Ja", value: YesNo.yes },
          { label: "Nein", value: YesNo.no },
        ],
        name: "hasSpecificProperty",
        label: t("has_specific_property"),
        rules: {
          required: {
            value: true,
            message: t("has_specific_property_error"),
          },
        },
      },
    );
  }

  return {
    submit: true,
    title: t("federal_state_title"),
    fields,
  };
};

export const getEmploymentTypeStep = (
  t: TFunction,
): FormDefinition<BudgetCheckFormValues> => {
  return {
    submit: true,
    title: t("employment_type_title"),
    fields: [
      {
        label: t("employment_type_label"),
        component: "SELECT",
        name: "employmentType",
        defaultValue: "EMPLOYED",
        options: Object.keys(employmentTypeLabels).map((key) => ({
          value: key,
          label: employmentTypeLabels[key],
        })),
        rules: {
          required: { value: true, message: t("employment_type_error") },
        },
      },
    ],
  };
};

export const getSelfEmployedIncomeStep = (
  t: TFunction,
): FormDefinition<BudgetCheckFormValues> => {
  return {
    submit: true,
    title: t("income_title"),
    fields: [
      {
        label: t("yearly_net_income_label"),
        component: "NUMBER_INPUT",
        inputMode: "numeric",
        name: "yearlyNetIncome",
        suffix: "€",
        placeholder: t("yearly_net_income_placeholder"),
        rules: {
          required: { value: true, message: t("yearly_net_income_error") },
        },
      },
      {
        label: t("self_employment_start_label"),
        component: "MONTH",
        type: "month",
        name: "employmentStart",
        placeholder: t("employment_start_placeholder"),
        rules: {
          required: { value: true, message: t("self_employment_start_error") },
        },
      },
      {
        label: t("optional_label", { label: t("existing_debt_label") }),
        component: "NUMBER_INPUT",
        inputMode: "numeric",
        name: "existingDebt",
        suffix: "€",
        placeholder: t("existing_debt_placeholder"),
      },
    ],
  };
};

export const getEmployedIncomeStep = (
  t: TFunction,
): FormDefinition<BudgetCheckFormValues> => {
  return {
    submit: true,
    title: t("income_title"),
    fields: [
      {
        label: t("monthly_net_income_label"),
        component: "NUMBER_INPUT",
        inputMode: "numeric",
        name: "monthlyNetIncome",
        suffix: "€",
        placeholder: t("monthly_net_income_placeholder"),
        rules: {
          required: { value: true, message: t("monthly_net_income_error") },
        },
      },
      {
        label: t("optional_label", { label: t("existing_debt_label") }),
        component: "NUMBER_INPUT",
        inputMode: "numeric",
        name: "existingDebt",
        suffix: "€",
        placeholder: t("existing_debt_placeholder"),
      },
    ],
  };
};

export const getRetiredIncomeStep = (
  t: TFunction,
): FormDefinition<BudgetCheckFormValues> => {
  return {
    submit: true,
    title: t("income_title"),
    fields: [
      {
        label: t("pension_label"),
        component: "NUMBER_INPUT",
        inputMode: "numeric",
        suffix: "€",
        name: "monthlyNetIncome",
        placeholder: t("pension_placeholder"),
        rules: {
          required: { value: true, message: t("pension_error") },
        },
      },
      {
        label: t("optional_label", { label: t("existing_debt_label") }),
        component: "NUMBER_INPUT",
        inputMode: "numeric",
        name: "existingDebt",
        suffix: "€",
        placeholder: t("existing_debt_placeholder"),
      },
    ],
  };
};

export const getEquityStep = (
  t: TFunction,
): FormDefinition<BudgetCheckFormValues> => {
  return {
    submit: true,
    title: t("equity_title"),
    fields: [
      {
        label: t("equity_label"),
        component: "NUMBER_INPUT",
        inputMode: "numeric",
        name: "equity",
        placeholder: t("equity_placeholder"),
        suffix: "€",
        rules: {
          required: { value: true, message: t("equity_error") },
        },
      },
      {
        component: "RADIO_CARD",
        options: [
          { label: "Ja", value: YesNo.yes },
          { label: "Nein", value: YesNo.no },
        ],
        name: "wantsToSellAProperty",
        label: t("wants_to_sell_another_property"),
        rules: {
          required: {
            value: true,
            message: t("wants_to_sell_another_property_error"),
          },
        },
      },
    ],
  };
};
